import { Component, Inject, Injector, OnInit, AfterViewInit, OnDestroy, NgZone } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationService } from '@core/authentication/services';
import { LoginErrorCode } from '@core/constants/login-error-code';
import { IEnvironmentVariables } from '@env/IEnvironment';
import { b2cPolicies } from '@env/environment';
import { ENV } from '@env/environment.token';
import { SecurityService } from '@mynexus/mynexus-ui-lib';
import { SharedServiceComponent } from '@shared/components/shared-service/shared-service.component';
import { AccessibilityObserverService } from '@core/services/accessibility-observer.service';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'Code Management';
  initComplete = false;

  constructor(
    private readonly msalService: MsalService,
    private readonly authenticationService: AuthenticationService,
    private readonly securityService: SecurityService,
    private readonly injector: Injector,
    private readonly ngZone: NgZone,
    private readonly accessibilityObserverService: AccessibilityObserverService,
    @Inject(ENV) private readonly environment: IEnvironmentVariables
  ) {
    customElements.define(
      'mnc-shared-services',
      createCustomElement(SharedServiceComponent, { injector: this.injector })
    );
  }

  ngOnInit(): void {
    this.msalService.handleRedirectObservable().subscribe({
      next: authResult => {
        if (
          authResult &&
          authResult.idTokenClaims['tfp' as keyof object] ===
            b2cPolicies.names.forgotPassword
        ) {
          this.msalService.logout();
        } else if (this.authenticationService.user) {
          this.initializePermission();
        }
      },
      error: error => {
        this.handleLoginError(error);
      },
    });
  }

  ngAfterViewInit(): void {
    this.ngZone.onStable.pipe(map(() => true)).subscribe(() => {
      this.accessibilityObserverService.start();
    });
  }

  ngOnDestroy(): void {
    this.accessibilityObserverService.stop();
  }

  private handleLoginError(error: { errorMessage: string[] }): void {
    if (!error) {
      return;
    }

    const USER_FORGOT_PASSWORD_CODE = LoginErrorCode.ForgotPassword;
    const USER_CANCEL_FLOW_CODE = LoginErrorCode.CancelFlow;

    if (error.errorMessage.includes(USER_FORGOT_PASSWORD_CODE)) {
      this.msalService.loginRedirect({
        authority: `${b2cPolicies.authorityBaseUrl}/${b2cPolicies.names.forgotPassword}`,
        scopes: this.environment.authConfig.angularConfig.consentScopes,
      });
    }

    if (error.errorMessage.includes(USER_CANCEL_FLOW_CODE)) {
      this.msalService.loginRedirect({
        authority: `${b2cPolicies.authorityBaseUrl}/${b2cPolicies.names.signUpSignIn}`,
        scopes: this.environment.authConfig.angularConfig.consentScopes,
      });
    }
  }

  private initializePermission(): void {
    this.authenticationService.getPermissions().subscribe({
      next: permissions => {
        this.securityService.setUserPermissions(
          Object.values(permissions).flat()
        );
        this.securityService.setAllowEmptyPermission(false);
        this.initComplete = true;
      },
    });
  }
}
